import {useCookieConsentContext} from '@use-cookie-consent/react';

import type {HandleFormSubmit} from 'Shared/formHelperTypes';

/**
 * Custom hook for managing cookie consent modal functionality and form submission.
 * This hook provides handlers for accepting/declining cookies and closing the consent modal.
 * It integrates with the cookie consent context to manage user preferences for analytics and necessary cookies.
 *
 * @param closeModal Function to close the consent modal, optionally accepting an onClose callback.
 * @returns Object containing consent state and handler functions for form submission and cancellation.
 *
 * @example
 * ```tsx
 * const ConsentModal = ({ onClose }) => {
 *     const { consent, handleSubmit, handleCancel } = useConsentManager(onClose);
 *
 *     return (
 *         <form onSubmit={handleSubmit}>
 *             <input type="checkbox" name="analytics" />
 *             <button type="submit">Accept</button>
 *             <button type="button" onClick={handleCancel}>Cancel</button>
 *         </form>
 *     );
 * };
 * ```
 */
export const useConsentManager = (closeModal: (onClose?: () => void) => void) => {
    const {acceptCookies, consent} = useCookieConsentContext();

    /**
     * Handles form submission for cookie consent preferences.
     * This function processes the form values to accept necessary cookies and conditionally accept analytics cookies based on user selection.
     * After updating consent preferences, it automatically closes the modal.
     *
     * @param props        The form submission props.
     * @param props.values Object containing the analytics consent preference.
     * @returns Promise that resolves when the consent preferences are saved and modal is closed.
     *
     * @example
     * ```tsx
     * const formData = { values: { analytics: true } };
     * await handleSubmit(formData);
     * ```
     */
    // eslint-disable-next-line @typescript-eslint/require-await
    const handleSubmit: HandleFormSubmit<{analytics: boolean}> = async ({values}) => {
        acceptCookies({
            necessary: true,
            statistics: values.analytics
        });
        closeModal();
    };

    /**
     * Handles cancellation of the consent modal without saving any preferences.
     * This function simply closes the modal without modifying existing cookie consent settings.
     * It provides users with an option to dismiss the consent modal without making changes.
     *
     * @example
     * ```tsx
     * <button onClick={handleCancel}>Cancel</button>
     * ```
     */
    const handleCancel = () => {
        closeModal();
    };

    return {
        consent,
        handleCancel,
        handleSubmit
    };
};